import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Document, Page, pdfjs } from 'react-pdf';
import { API_ENDPOINTS } from './config';
import {
  FiArrowLeft, FiDownload, FiActivity, FiSend, FiClock, FiCheckCircle, FiX,
  FiShield, FiLock, FiUser,
} from 'react-icons/fi';
import SigningLoader from './SigningLoader';
import './App.css';
import './Dashboard.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

function DocumentDetailPage() {
  const { referenceId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    axios
      .get(`${baseUrl}/documents/${referenceId}`)
      .then((res) => {
        setDoc(res.data);
        setError('');
      })
      .catch((err) => {
        setError(err.response?.data?.error || 'Failed to load document');
        setDoc(null);
      })
      .finally(() => setLoading(false));
  }, [referenceId]);

  const getPdfUrl = () => {
    if (!doc) return null;
    const fp = doc.last_signed_file_path || doc.file_path;
    if (!fp) return null;
    if (typeof fp === 'object' && fp.url) return fp.url;
    return `${API_ENDPOINTS.LOGIN.split('/auth')[0]}/files/uploads/${fp}`;
  };

  const getDownloadUrl = () => {
    const fp = doc?.last_signed_file_path || doc?.file_path;
    if (!fp) return null;
    if (typeof fp === 'object' && fp.url) return fp.url;
    return `${API_ENDPOINTS.LOGIN.split('/auth')[0]}/files/uploads/${fp}`;
  };

  const getAuditUrl = () => {
    const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
    return `${baseUrl}/signing/download_audit/${referenceId}`;
  };

  const handleResendNotification = async () => {
    setResendLoading(true);
    try {
      const baseUrl = API_ENDPOINTS.LOGIN.split('/auth')[0];
      await axios.post(`${baseUrl}/requests/resend_notification`, { reference_id: referenceId });
      alert('Notification(s) sent to pending recipients.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to resend notification.');
    } finally {
      setResendLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
          <SigningLoader size="lg" label="Loading document details..." />
        </div>
      </div>
    );
  }
  if (error || !doc) {
    return (
      <div className="app-container" style={{ padding: 24 }}>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
          <FiArrowLeft style={{ marginRight: 8 }} /> Back
        </button>
        <p style={{ color: '#ef4444' }}>{error || 'Document not found'}</p>
      </div>
    );
  }

  const pdfUrl = getPdfUrl();
  const downloadUrl = getDownloadUrl();
  const security = doc.security_settings || {};
  const recipients = doc.recipients || [];

  return (
    <div className="app-container" style={{ background: 'var(--black-bg-base)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
          <FiArrowLeft style={{ marginRight: 8 }} /> Back
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-on-black)' }}>
              {doc.document_name || 'Document'}
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--text-on-black-muted)', fontFamily: 'monospace' }}>
              Reference: {doc.reference_id}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {downloadUrl && (
              <a href={downloadUrl} className="btn-action btn-download" target="_blank" rel="noreferrer">
                <FiDownload /> Download
              </a>
            )}
            <a href={getAuditUrl()} className="btn-action btn-audit" target="_blank" rel="noreferrer">
              <FiActivity /> Audit trail
            </a>
            <button
              type="button"
              className="btn-action btn-sign"
              onClick={handleResendNotification}
              disabled={resendLoading || !recipients.some((r) => r.status === 'Pending')}
            >
              {resendLoading ? <SigningLoader size="sm" label="" /> : <><FiSend /> Resend notification</>}
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>
          {/* PDF Preview */}
          <section className="table-card" style={{ overflow: 'hidden' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>PDF Preview</h3>
            {pdfUrl ? (
              <div style={{ border: '1px solid var(--black-border)', borderRadius: 8, overflow: 'auto', background: 'var(--black-bg-card-2)' }}>
                <Document file={pdfUrl} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
                  <Page pageNumber={pageNumber} width={500} renderTextLayer={false} renderAnnotationLayer={false} />
                </Document>
                {numPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, justifyContent: 'center' }}>
                    <button className="btn btn-secondary btn-sm" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => p - 1)}>Prev</button>
                    <span style={{ color: 'var(--text-on-black-secondary)' }}>Page {pageNumber} of {numPages}</span>
                    <button className="btn btn-secondary btn-sm" disabled={pageNumber >= numPages} onClick={() => setPageNumber((p) => p + 1)}>Next</button>
                  </div>
                )}
              </div>
            ) : (
              <p style={{ color: 'var(--text-on-black-muted)' }}>No file available.</p>
            )}
          </section>

          {/* Recipients & Security */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <section className="table-card">
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiUser /> Recipients
              </h3>
              <div className="recipient-list-container">
                {recipients.map((r, idx) => {
                  const createdAt = doc.created_at ? new Date(doc.created_at).getTime() : null;
                  const signedAt = r.signed_at ? new Date(r.signed_at).getTime() : null;
                  const hoursAfterSent = (createdAt != null && signedAt != null && r.status === 'Signed')
                    ? (signedAt - createdAt) / (1000 * 60 * 60)
                    : null;
                  return (
                    <div key={r.email || idx} className="recipient-grid" style={{ marginBottom: 12 }}>
                      <div style={{ fontWeight: 600 }}>{r.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-on-black-muted)' }}>{r.email}</div>
                      <div className={`recipient-status ${r.status === 'Signed' ? 'status-signed' : r.status === 'Rejected' ? 'status-rejected' : 'status-pending'}`}>
                        {r.status === 'Signed' ? <FiCheckCircle /> : r.status === 'Rejected' ? <FiX /> : <FiClock />}
                        {r.status}
                        {r.signed_at && (
                          <span style={{ fontSize: 11, color: 'var(--text-on-black-muted)', marginLeft: 6 }}>
                            {new Date(r.signed_at).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {hoursAfterSent != null && (
                        <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-on-black-muted)', marginTop: 4 }}>
                          Signed {Number(hoursAfterSent.toFixed(1))} hours after request was sent
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="table-card">
              <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <FiShield /> Security settings
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FiLock size={14} />
                  <span>Email notifications: {security.email_notification_enabled !== false ? 'On' : 'Off'}</span>
                </div>
                {security.capture_location && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiLock size={14} /> Location capture: On</div>}
                {security.capture_face && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiLock size={14} /> Face capture: On</div>}
                {security.otp_enabled && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiLock size={14} /> OTP verification: On</div>}
                {security.expiry_date && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FiClock size={14} /> Expiry: {new Date(security.expiry_date).toLocaleDateString()}</div>}
                {!security.email_notification_enabled && !security.capture_location && !security.capture_face && !security.otp_enabled && !security.expiry_date && (
                  <span style={{ color: 'var(--text-on-black-muted)' }}>Default settings</span>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentDetailPage;
